import type { z } from 'zod';

/** A flat map of environment variables, e.g. `process.env` or a Worker `env`. */
export type EnvSource = Readonly<Record<string, string | undefined>>;

export interface EnvIssue {
  /** Variable name, e.g. `API_URL`. */
  name: string;
  kind: 'missing' | 'invalid';
  /** Text from the schema's `.describe()` — by convention "service + link". */
  description: string | undefined;
  /** Validation message from zod (for `invalid`) or "required" (for `missing`). */
  message: string;
}

export class EnvError extends Error {
  readonly issues: readonly EnvIssue[];

  constructor(issues: readonly EnvIssue[]) {
    super(formatEnvIssues(issues));
    this.name = 'EnvError';
    this.issues = issues;
  }
}

function defaultSource(): EnvSource {
  return typeof process === 'undefined' ? {} : process.env;
}

function describeKey(schema: z.ZodObject, name: string): string | undefined {
  const field = (schema.shape as Record<string, z.ZodType | undefined>)[name];
  return field?.description;
}

/**
 * Pick only the schema's keys from `source`, treating empty strings as unset
 * (a `FOO=` line in a .env file means "not configured").
 */
function pick(schema: z.ZodObject, source: EnvSource): Record<string, string | undefined> {
  const picked: Record<string, string | undefined> = {};
  for (const name of Object.keys(schema.shape)) {
    const value = source[name];
    picked[name] = value === '' ? undefined : value;
  }
  return picked;
}

/** Turn a zod error into one issue per variable (first problem per name wins). */
export function collectEnvIssues(
  schema: z.ZodObject,
  input: Readonly<Record<string, string | undefined>>,
  error: z.ZodError,
): EnvIssue[] {
  const byName = new Map<string, EnvIssue>();
  for (const issue of error.issues) {
    const name = String(issue.path[0] ?? '(root)');
    if (byName.has(name)) continue;
    const missing = input[name] === undefined;
    byName.set(name, {
      name,
      kind: missing ? 'missing' : 'invalid',
      description: describeKey(schema, name),
      message: missing ? 'required' : issue.message,
    });
  }
  return [...byName.values()];
}

/** Human-readable report naming every missing or invalid variable. */
export function formatEnvIssues(issues: readonly EnvIssue[]): string {
  const lines = issues.map((issue) => {
    const what = issue.kind === 'missing' ? 'missing' : `invalid: ${issue.message}`;
    const help = issue.description ? ` — ${issue.description}` : '';
    return `  - ${issue.name} (${what})${help}`;
  });
  return [
    `Environment check failed: ${issues.length} variable(s) missing or invalid.`,
    ...lines,
    'Set them in the root .env.local (see docs/services.md), then run `pnpm env:sync`.',
  ].join('\n');
}

/**
 * Validate environment variables against a zod object schema.
 * Returns the parsed values, or throws an `EnvError` listing every
 * missing/invalid variable with its `.describe()` text.
 */
export function defineEnv<S extends z.ZodObject>(
  schema: S,
  source: EnvSource = defaultSource(),
): z.output<S> {
  const input = pick(schema, source);
  const result = schema.safeParse(input);
  if (result.success) return result.data;
  throw new EnvError(collectEnvIssues(schema, input, result.error));
}
